
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  Package,
  Calendar,
  DollarSign,
  ChevronRight,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  RefreshCw,
  CreditCard,
  Repeat,
  Loader2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Order } from "../OrderHistory";
import SwipeableCard from "@/components/mobile/SwipeableCard";

const getStatusAppearance = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'delivered':
      return { icon: <CheckCircle className="h-4 w-4" />, color: "text-green-400", bgColor: "bg-green-400/10" };
    case 'processing':
      return { icon: <RefreshCw className="h-4 w-4 animate-spin" />, color: "text-blue-400", bgColor: "bg-blue-400/10" };
    case 'shipped':
      return { icon: <Truck className="h-4 w-4" />, color: "text-purple-400", bgColor: "bg-purple-400/10" };
    case 'cancelled':
      return { icon: <XCircle className="h-4 w-4" />, color: "text-red-400", bgColor: "bg-red-400/10" };
    case 'pending':
      return { icon: <Clock className="h-4 w-4" />, color: "text-yellow-400", bgColor: "bg-yellow-400/10" };
    default:
      return { icon: <Package className="h-4 w-4" />, color: "text-gray-400", bgColor: "bg-gray-400/10" };
  }
};

const getPaymentStatusAppearance = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return { icon: <CheckCircle className="h-4 w-4" />, color: "text-green-400" };
      case 'pending':
        return { icon: <Clock className="h-4 w-4" />, color: "text-yellow-400" };
      case 'failed':
        return { icon: <XCircle className="h-4 w-4" />, color: "text-red-400" };
      default:
        return { icon: <CreditCard className="h-4 w-4" />, color: "text-gray-400" };
    }
};

interface OrderCardProps {
  order: Order;
  onViewDetails: (orderId: string) => void;
  onReorder: (orderId: string) => void;
  isReordering: boolean;
  isMobile?: boolean;
}

const OrderCard = ({ order, onViewDetails, onReorder, isReordering, isMobile }: OrderCardProps) => {
  const { icon: statusIcon, color: statusColor, bgColor: statusBgColor } = getStatusAppearance(order.status);
  const { icon: paymentStatusIcon, color: paymentStatusColor } = getPaymentStatusAppearance(order.payment_status);

  const cardContent = (
    <Card variant="glass" className="border-white/10 backdrop-blur-md hover:border-white/20 transition-all duration-300 overflow-hidden">
      <div className="p-4">
        <div className="flex flex-col sm:flex-row justify-between sm:items-start mb-3">
          <div className="flex items-center gap-3 mb-2 sm:mb-0">
            <div className={`p-2 rounded-full ${statusBgColor} self-start mt-1`}>
              <div className={statusColor}>{statusIcon}</div>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-card-foreground">Order #{order.order_number}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(order.created_at), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-start">
            <Badge variant="outline" className={`capitalize border-0 ${statusBgColor} ${statusColor}`}>{order.status}</Badge>
          </div>
        </div>

        {order.order_items && order.order_items.length > 0 && (
          <div className="pl-12 pb-3">
            <div className="flex items-center">
              <div className="flex -space-x-4">
                {order.order_items.slice(0, 4).map((item, index) =>
                  item.products?.image_url ? (
                    <img
                      key={index}
                      src={item.products.image_url}
                      alt="product"
                      className="h-10 w-10 rounded-full border-2 border-background bg-muted object-cover shadow-md"
                    />
                  ) : (
                    <div key={index} className="h-10 w-10 rounded-full border-2 border-background bg-muted flex items-center justify-center shadow-md">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )
                )}
              </div>
              {order.order_items.length > 4 && (
                <div className="flex items-center justify-center h-10 w-10 rounded-full border-2 border-background bg-muted text-xs font-semibold text-card-foreground shadow-md -ml-4 z-10">
                  +{order.order_items.length - 4}
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-4 text-sm border-t border-border/10 pt-3">
          <div>
            <p className="text-muted-foreground text-xs">Amount</p>
            <p className="font-semibold text-card-foreground flex items-center gap-1">
              <DollarSign className="h-4 w-4 text-green-400" />
              {Number(order.total_amount).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Items</p>
            <p className="font-semibold text-card-foreground flex items-center gap-1">
              <Package className="h-4 w-4 text-blue-400" />
              {order.order_items_count || 0}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Payment</p>
            <p className={`font-semibold capitalize flex items-center gap-1 ${paymentStatusColor}`}>
              {paymentStatusIcon}
              {order.payment_status}
            </p>
          </div>
          {order.tracking_number && (
              <div className="col-span-2 md:col-span-3">
                  <p className="text-muted-foreground text-xs">Tracking</p>
                  <p className="font-mono text-sm text-card-foreground">{order.tracking_number}</p>
              </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 mt-4">
          {isMobile ? (
             <p className="text-xs text-muted-foreground/70 text-right w-full pr-2">
              Swipe for actions
            </p>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReorder(order.id)}
                disabled={isReordering}
                className="text-muted-foreground hover:text-card-foreground hover:bg-white/10"
              >
                {isReordering ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Repeat className="h-4 w-4 mr-2" />
                )}
                Reorder
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onViewDetails(order.id)} className="text-muted-foreground hover:text-card-foreground hover:bg-white/10">
                View Details
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`flex-grow ${isMobile ? '' : 'pl-8'}`}
    >
      {isMobile ? (
        <SwipeableCard
          onSwipeLeft={() => onViewDetails(order.id)}
          onSwipeRight={!isReordering ? () => onReorder(order.id) : undefined}
        >
          {cardContent}
        </SwipeableCard>
      ) : (
        cardContent
      )}
    </motion.div>
  );
};
export default OrderCard;
